<template>
  <div class="w-full flex justify-center gap-2">
    <div class="glass-frosted rounded-8xl w-83 h-67.5 p-4">
      <div class="flex justify-end">
        <Button
          color="glass"
          class="w-12 h-6"
          size="sm"
          :active="!!currentSelection"
          :disabled="!currentSelection"
          @click="handleTakeEffect"
        >
          {{ $t('keys.tgl.apply') }}
        </Button>
      </div>
      <div class="flex justify-center items-center gap-8">
        <div class="flex flex-col items-center">
          <div
            class="relative flex flex-col items-center text-text-important-default"
            @dragover="handleDragOver"
            @drop="handleDrop"
          >
            <div
              class="flex justify-center items-center w-14 h-14 px-0.5 text-black-white rounded-xl border border-solid bg-key cursor-pointer transition-all border-brand-blue1"
            >
              <span class="w-full text-center text-l-medium leading-4 line-clamp-2">
                {{ currentSelectionLabel }}
              </span>
            </div>
            <Icon
              v-show="currentSelection"
              icon="Function-Fill-Close"
              class="absolute -top-1 -right-1 w-4 h-4 text-brand-red cursor-pointer"
              @click.stop="currentSelection = null"
            />
          </div>
          <span class="text-m-medium text-black-white">{{ $t('keys.tgl.sustainedTrigger') }}</span>
        </div>
      </div>
      <div class="mt-2">
        <span class="text-m-medium text-black-white">{{ $t('keys.tgl.holdTime') }}</span>
        <div class="flex items-center gap-2 mt-2">
          <Slider v-model="triggerInput" :min="10" :max="2000" />
          <span class="text-s-medium text-black-white">{{ triggerInput[0] }}ms</span>
        </div>
        <div class="mt-2 text-text-glass-default text-s-medium">
          <span>{{ $t('keys.tgl.description') }}</span>
          <div class="flex mt-2">
            <Icon icon="Function-FiII-Description" class="w-4 h-4 mt-0.5 mr-0.5" />
            <span>{{ $t('keys.tgl.guide') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4 font">
      <div class="p-2">
        <Keyboard
          key-class="flex items-center justify-center gap-1 bg-key text-black-white font-[Barlow] text-xs-semibold! whitespace-nowrap transition-colors cursor-pointer hover:text-text-glass-default active:bg-white active:text-brand-blue2 disabled:text-glass-disabled disabled:cursor-not-allowed disabled:pointer-events-none"
          :keyboard-config="keyboardConfig!"
          :native-event-names="sourceKeyboardEventNames"
          :gap="6"
          @key-event="handleSourceKeyEvent"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, inject, watch, watchEffect } from 'vue'
  import { Keyboard, Icon, Slider, Button, toast } from '@rapoo/element'
  import { getDragData, setDragData, DragType } from '@/utils/dnd'
  import { useLocaleStore } from '@/stores'
  import { useDragImage } from '../composables/useDragImage'
  import { fullSize108 } from '@/utils/fullSize108'
  import {
    KEYCODE_KEYWORDS_MAP,
    getLocalizedField,
    getKeyConfigByTypeCode,
  } from '../utils/keyMapSettings'
  import { findSettingByTargetKey, parseNumber } from '../utils/keyIdentity'
  import { SelectedKeySymbol, KeySettingsSymbol, UpdateSelectedKeyMappingSymbol } from '../types'

  import type {
    KeyboardConfig,
    KeyboardKeyEventPayload,
    KeyEventName,
    KeyConfig,
  } from '@rapoo/element'
  import type { DualModeKey } from '@rapoo/device-sdk'
  import type { KeyChangePayload, UpdateSelectedKeyMapping } from '../types'

  const { t } = useLocaleStore()

  /** 单键在 keycode 元数据中的 type（与 dualModeKey.clickType/longPressType 含义不同） */
  const SINGLE_KEY_META_TYPE = 0x01

  const sourceKeyboardEventNames: KeyEventName[] = ['click', 'dragstart']

  const selectedKey = inject(SelectedKeySymbol)
  const keySettings = inject(KeySettingsSymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)

  const { setDragImage } = useDragImage()

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    const isCybercafe = (import.meta.env.VITE_CHANNEL as string) === 'china_cybercafe'

    return {
      ...fullSize108,
      keys: fullSize108.keys.map((row) =>
        row
          // 网吧版常规键盘去掉 Fn 源键
          .filter((key) => !(isCybercafe && key.keywords === 'Fn'))
          .map((key) => {
            const { w, h, x, y, keywords, ...rest } = key
            const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

            return {
              ...rest,
              ...config,
              rect: {
                w,
                h,
                x,
                y,
              },
            }
          }),
      ),
    }
  })

  // 触发按住时间
  const triggerInput = ref([1000])

  // 左侧框当前 pick 的按键
  const currentSelection = ref<KeyConfig | null>(null)

  const currentSelectionLabel = computed(() => {
    if (currentSelection.value) {
      return getLocalizedField(currentSelection.value.alias || currentSelection.value.name)
    }

    return ''
  })

  /**
   * 从 dualModeKey 回填功能框与触发时间。
   * TGL 单击/长按为同一键，取 clickKeyCode 即可。
   */
  const restoreSelectionFromDualModeKey = (dualModeKey: DualModeKey) => {
    currentSelection.value =
      getKeyConfigByTypeCode(SINGLE_KEY_META_TYPE, dualModeKey.clickKeyCode) ?? null
    if (dualModeKey.time != null) {
      triggerInput.value = [dualModeKey.time]
    }
  }

  // 选中已是 TGL 的按键时回显；取消选中/切键不清空，离开 tab 卸载后自然清空
  watch(
    () => selectedKey?.value,
    (key) => {
      if (!key || !keySettings?.value) return

      const setting = findSettingByTargetKey(keySettings.value, key)
      if (setting?.keyType !== 'TGL' || !setting.raw?.dualModeKey) return

      restoreSelectionFromDualModeKey(setting.raw.dualModeKey as DualModeKey)
    },
    { immediate: true },
  )

  // 监听虚拟键盘点击选中后，如果功能框已填，则唤出toast
  watchEffect(() => {
    if (selectedKey && selectedKey.value && currentSelection.value) {
      toast.info(t('toast.keys.tglApply'))
    }
  })

  // 功能框 拖拽进入
  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  // 功能框 拖拽放下
  const handleDrop = (event: DragEvent) => {
    event.preventDefault()

    const dataTransfer = event.dataTransfer
    if (!dataTransfer) return
    const draggedData = getDragData(dataTransfer, DragType.KEY_MAPPING)

    if (draggedData) {
      currentSelection.value = {
        ...(draggedData as any),
      }
    }
  }

  // 右侧键盘事件处理
  const handleSourceKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (eventName === 'click') {
      currentSelection.value = data

      return
    }

    if (eventName === 'dragstart') {
      const event = nativeEvent as DragEvent

      const dataTransfer = event.dataTransfer
      if (!dataTransfer) return

      const dragData: KeyConfig = data

      setDragData(dataTransfer, DragType.KEY_MAPPING, dragData as any)

      setDragImage(event, {
        text: getLocalizedField(data.alias || data.name),
      })
    }
  }

  // 点击生效配置
  const handleTakeEffect = () => {
    if (!currentSelection.value) {
      toast.info(t('toast.keys.tglSelect'))
      return
    }

    if (selectedKey && selectedKey.value && updateSelectedKeyMapping) {
      // type 目前只有单键类型0x00，[01,02,03,04]后面再补充
      const dualModeKey: DualModeKey = {
        time: triggerInput.value[0] ?? 1000,
        clickKeyCode: parseNumber(currentSelection.value.code)!,
        clickType: 0x00,
        longPressKeyCode: parseNumber(currentSelection.value.code)!,
        longPressType: 0x00,
      }

      const payload: KeyChangePayload = {
        keywords: selectedKey.value.keywords,
        keyType: 'TGL',
        target: null,
        raw: {
          type: '0x0b',
          dualModeKey,
          clickKeyLabel: getLocalizedField(
            currentSelection.value.alias || currentSelection.value.name,
          ),
        },
      }

      updateSelectedKeyMapping(payload)
      // 改键成功后按键仍保持选中，保留功能框内容以便回显/再次调整
      toast.dismiss('keyboard')
    }
  }
</script>

<style scoped></style>
